import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('danger');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { isAuthenticated, resetPassword } = useContext(AuthContext);
  const navigate = useNavigate();
  const { token } = useParams();

  const { password, confirmPassword } = formData;

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    
    // Validate token format
    if (!token) {
      setAlertType('danger');
      setAlertMessage('Invalid password reset link');
    }
  }, [isAuthenticated, navigate, token]);

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear field error when user types
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setAlertMessage('');
    
    const result = await resetPassword(token, password);
    
    setIsSubmitting(false);
    
    if (result.success) {
      setAlertType('success');
      setAlertMessage('Your password has been reset successfully. You can now login with your new password.');
      setIsSubmitted(true);
    } else {
      setAlertType('danger');
      setAlertMessage(result.error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-logo">
        <h1>Optical Data Manager</h1>
      </div>
      <Card>
        <Card.Body>
          <h2 className="text-center mb-4">
            <FaLock className="me-2" />
            Reset Password
          </h2>
          
          {alertMessage && (
            <Alert variant={alertType} onClose={() => setAlertMessage('')} dismissible>
              {alertMessage}
            </Alert>
          )}
          
          {!isSubmitted ? (
            <Form onSubmit={onSubmit}>
              <p className="text-muted mb-4">
                Enter your new password below.
              </p>
              
              <Form.Group className="mb-3" controlId="password">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={password}
                  onChange={onChange}
                  placeholder="Enter new password"
                  isInvalid={!!formErrors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.password}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={onChange}
                  placeholder="Confirm new password"
                  isInvalid={!!formErrors.confirmPassword}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mt-3" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Form>
          ) : (
            <div className="text-center">
              <p>Your password has been reset successfully.</p>
              <Button 
                variant="primary" 
                as={Link}
                to="/login"
              >
                Go to Login
              </Button>
            </div>
          )}
          
          <hr />
          
          <div className="text-center">
            <Link to="/login">Back to Login</Link>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ResetPassword;
